import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  UseInfiniteQueryOptions,
  QueryKey,
  QueryFunctionContext,
} from "@tanstack/react-query";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Variables, request } from "graphql-request";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";

export type GraphQLInfiniteQueryParams<TQueryResult, TVariables, TData, TError> = {
  url: string;
  queryKey: QueryKey;
  document: TypedDocumentNode<TQueryResult, TVariables> | string;
  options?: Omit<UseInfiniteQueryOptions<TQueryResult, TError, TData>, "queryKey" | "queryFn">;
  requestHeaders?: Record<string, string>;
  getVariables: (params: QueryFunctionContext<QueryKey, number>) => TVariables;
  getAccessToken?: () => Promise<string>;
};

// Returns query options
export function getGraphQLInfiniteQuery<
  TQueryResult,
  TVariables extends Variables,
  TData = TQueryResult,
  TError = Error,
>({
  url,
  queryKey,
  document,
  options = {},
  requestHeaders = {},
  getVariables,
  getAccessToken,
}: GraphQLInfiniteQueryParams<TQueryResult, TVariables, TData, TError>): UseInfiniteQueryOptions<
  TQueryResult,
  TError,
  TData
> {
  const queryFn = async (paginationParams: QueryFunctionContext<QueryKey, number>) => {
    let authToken: string | undefined;
    if (getAccessToken) authToken = await getAccessToken();
    return request<TQueryResult>({
      url,
      document,
      variables: getVariables(paginationParams),
      requestHeaders: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        "X-REQUEST-ID": uuidv4(),
        ...requestHeaders,
      },
    });
  };

  return { queryKey, queryFn, ...options };
}

export function useGraphQLInfiniteQuery<
  TQueryResult,
  TVariables extends Variables,
  TData = TQueryResult,
  TError = Error,
>(
  params: Omit<GraphQLInfiniteQueryParams<TQueryResult, TVariables, TData, TError>, "getAccessToken">,
): UseInfiniteQueryResult<TData, TError> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const query = getGraphQLInfiniteQuery<TQueryResult, TVariables, TData, TError>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useInfiniteQuery<TQueryResult, TError, TData>(query);
}
